import { FontSizeEnum, IconSizeEnum } from '@acceso/design-system';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';

import { Clock } from '@/components/Clock';
import { HelpTour } from '@/components/HelpTour/HelpTour';
import { Shorcut } from '@/components/Shorcut/Shorcut';
import { SEGMENT_REVIEW_STATES } from '@/constants';
import { BroadcastScheduleProvider } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/components/BroadcastScheduleProgramBodySegmentToolkitLogotipos/context/BroadcastScheduleProvider';
import { ESegmentToolkit } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/types';
import { BroadcastScheduleProgramHeaderButtonFilters } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramHeaderButtonFilters/BroadcastScheduleProgramHeaderButtonFilters';
import { BroadcastScheduleProgramHeaderFilters } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramHeaderFilters/BroadcastScheduleProgramHeaderFilters';
import { BroadcastScheduleProgramHeaderSearch } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramHeaderSearch/BroadcastScheduleProgramHeaderSearch';
import { useReportDrawerController } from '@/features/broadcast-schedule-program/components/ReportDrawer/hooks/useReportDrawerControllers';
import { ReportDrawer } from '@/features/broadcast-schedule-program/components/ReportDrawer/ReportDrawer';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useDropInactiveFilterSelections } from '@/features/mentions/components/MentionFilters/active-options/use-drop-inactive-filter-selections';
import { MentionShowSelectedFilters } from '@/features/mentions/components/MentionFilters/components/MentionShowSelectedFilters/MentionShowSelectedFilters';
import { useSegmentsTour } from '@/features/segments/hooks/use-segments-tour';
import { useDebouncedBroadcastFilters } from '@/hooks/use-debounced-broadcast-filters';
import { useProgramBroadcastFilters } from '@/stores/program-broadcast-filters';
import { useReport } from '@/stores/report-store';

import { useInfiniteSegments } from '../api/get-infinite-segments';
import { segmentsQueryKeys } from '../api/query-keys';
import { SegmentBody } from '../components/SegmentBody/SegmentBody';
import { SegmentsHeaderName } from '../components/SegmentsHeaderName/SegmentsHeaderName';

export default function SegmentsPage() {
  const {
    isHeaderVisible,
    setActiveSegment,
    drawnFormReset,
    handleResetVisibleBroadcastProgramCards,
    videoControlRef,
    setSegmentItems,
    setVisibleBroadcastProgramCards,
    handleTooltikClick,
    activeSegment,
    pinnedId,
  } = useBroadcastScheduleProgram();
  const { report } = useReport();
  const { handleCreateReport, handleOnClose } = useReportDrawerController();
  const {
    logos,
    review_states,
    gte,
    lte,
    search,
    broadcasters,
    programs,
    people,
    channelType,
    startDatetimeGte,
    endDatetimeLte,
    workspaces,
    clients,
    projects,
    tags,
    startDatetimeLte,
    endDatetimeGte,
    audienceChannelSource,
    order_by,
    clearFilters,
  } = useProgramBroadcastFilters();
  const { startTour } = useSegmentsTour();
  useDropInactiveFilterSelections('segments');
  const searchParams = new URLSearchParams(location.search);
  const limits = searchParams.get('limit');
  const audienceChannelSourceFromUrl = searchParams.get('audience_channel_source') || '';
  const firstLoad = useRef(true);
  const queryKey = segmentsQueryKeys.infinite();

  const debounced = useDebouncedBroadcastFilters({
    logos,
    people,
    programs,
    broadcasters,
    workspaces,
    projects,
    tags,
  });

  const segmentInfiniteData = useInfiniteSegments({
    params: {
      startDatetimeGte,
      endDatetimeLte,
    },
    filters: {
      logos: debounced.logos,
      review_states,
      gte,
      lte,
      search,
      broadcasters: debounced.broadcasters,
      programs: debounced.programs,
      people: debounced.people,
      channelType,
      workspaces: debounced.workspaces,
      clients,
      projects: debounced.projects,
      tags: debounced.tags,
      startDatetimeLte,
      endDatetimeGte,
      audienceChannelSource: audienceChannelSourceFromUrl || audienceChannelSource,
      order_by,
    },
    limit: limits ? parseInt(limits, 10) : 25,
  });

  useEffect(() => {
    const data = segmentInfiniteData?.data?.pages?.flatMap(page => page.data) ?? null;

    if (!data) return;

    if (data.length === 0) {
      setActiveSegment(null);
      setVisibleBroadcastProgramCards(prev => ({
        ...prev,
        segments: true,
        segmentDetail: false,
      }));
      setSegmentItems([]);
      return;
    }

    const firstSegment = data[0];

    if (firstLoad.current) {
      if (!activeSegment) {
        setActiveSegment(firstSegment);
      }
      setSegmentItems(data);
      firstLoad.current = false;
      if (!pinnedId) {
        // TODO: Forcing LOGOS while development
        handleTooltikClick(ESegmentToolkit.LOGOTIPO);
      }

      return;
    }

    if (activeSegment && !data.some(segment => segment.id === activeSegment.id)) {
      setActiveSegment(firstSegment);
    }

    setSegmentItems(data);
  }, [segmentInfiniteData.data]);

  useEffect(() => {
    const primeraVez = localStorage.getItem('segmentTourVisto');
    if (!primeraVez) {
      startTour();
      setTimeout(() => {
        if (videoControlRef.current) {
          videoControlRef.current?.stop();
        }
      }, 2000);
      localStorage.setItem('segmentTourVisto', 'true');
    }
  }, []);

  useEffect(() => {
    return () => {
      clearFilters();
      setActiveSegment(null);
      drawnFormReset();
      handleResetVisibleBroadcastProgramCards();
    };
  }, [clearFilters]);

  return (
    <BroadcastScheduleProvider>
      <div className="flex flex-col gap-2" id="segments-page">
        <div className={clsx('flex gap-1', isHeaderVisible ? 'flex-col' : 'flex-col lg:flex-row')}>
          <SegmentsHeaderName />

          <div className="flex flex-col gap-2 lg:flex-row" id="segments-filter">
            {isHeaderVisible && <BroadcastScheduleProgramHeaderSearch />}
            {isHeaderVisible && (
              <BroadcastScheduleProgramHeaderFilters
                reviewState
                logo
                broadcast
                people
                workspaces
                tags
                programs
                reviewStateOptions={SEGMENT_REVIEW_STATES}
                activeOptionsSource="segments"
              />
            )}
          </div>
          <BroadcastScheduleProgramHeaderButtonFilters
            segments
            reviewStates={SEGMENT_REVIEW_STATES}
            queryKey={queryKey}
            isLoading={segmentInfiniteData.isLoading}
          />
          {!isHeaderVisible && (
            <div className="ml-4 flex items-center gap-2">
              <Clock fontSize={FontSizeEnum.Md} iconSize={IconSizeEnum.SM} />
              <Shorcut />
              <HelpTour tour={startTour} />
            </div>
          )}
        </div>
        {!isHeaderVisible && (
          <MentionShowSelectedFilters reviewStateOptions={SEGMENT_REVIEW_STATES} />
        )}
        <SegmentBody segmentsData={segmentInfiniteData} />
      </div>

      <ReportDrawer isOpen={report} onClose={handleOnClose} onSubmit={handleCreateReport} />
    </BroadcastScheduleProvider>
  );
}
